import {
  ActivityActor,
  ActivityActorType,
  ActivityCategory,
  ActivityEntity,
} from '@surecompanies/activity-log'
import { omit } from 'lodash'
import { createActivityLog } from 'modules/activity-log-service'
import { ACTIVITY_LOG_SOURCE } from 'modules/activity-log-service/constants'
import { storeInstance } from 'store/createStore'
import { ActivityReasonCategory } from 'types/activity'
import { openActivityLogPopup } from './activityLogPopupSlice'

/**
 * Retrieves the current application state including credentials, company, and person data
 * @param targetUserId - Optional user ID to get specific person data
 * @returns Object containing credentials, current company, and current person data
 * @throws Error if store is not initialized
 */
export const getCurrentState = (targetUserId?: string) => {
  if (!storeInstance) {
    throw new Error('Store not initialized')
  }

  const state = (storeInstance as any).getState()
  const currentCompanyId = state?.userSession?.currentCompanyId

  return {
    credentials: state?.authui?.credentials ?? {},
    currentCompany: state?.companies?.list[currentCompanyId],
    currentPerson: targetUserId
      ? state?.persons?.list[targetUserId]
      : undefined,
  }
}

/**
 * Extracts actor and target information for activity logging
 * @param targetUserId - Optional user ID to identify the target person
 * @returns Object containing actor details (ID, name, role, type) and target details (ID, name, type, role, company)
 */
export const getActorAndTarget = (
  targetUserId?: string,
  inputTargetName?: string
) => {
  const state = getCurrentState(targetUserId)
  const actor = state.credentials
  const { username: actorId, firstname, lastname, userRole: actorRole } = actor

  const actorName = `${firstname} ${lastname}`.trim()
  const currentCompany = state?.currentCompany
  const currentPerson = state?.currentPerson

  return {
    actorId,
    actorName,
    actorRole,
    actorType: ActivityActor.User,
    targetId: targetUserId ?? currentCompany?.id,
    targetName:
      inputTargetName ??
      (targetUserId ? currentPerson?.FullName : currentCompany?.companyName),
    targetType: targetUserId ? ActivityActor.User : ActivityActor.Company,
    targetRole: targetUserId ? currentPerson?.UserRole : undefined,
    currentCompany: {
      id: currentCompany?.id,
      name: currentCompany?.companyName,
    },
  }
}

/**
 * Posts an activity log entry with actor and target information
 * @param activity - Activity data including action, description, additional data, and optional target user ID
 * @returns Promise that resolves to the created activity log result, or undefined if creation fails
 */
export const postActivityLog = async (
  activity: Pick<
    ActivityEntity,
    'action' | 'description' | 'additionalData'
  > & {
    targetUserId?: string
    targetName?: string
    targetType?: ActivityActorType
  }
) => {
  try {
    const targetUserId = activity?.targetUserId
    const targetName = activity?.targetName
    const actorAndTarget = getActorAndTarget(targetUserId, targetName)
    const result = await createActivityLog({
      ...omit(actorAndTarget, 'currentCompany'),
      ...omit(activity, 'targetUserId'),
      targetType: activity?.targetType ?? actorAndTarget?.targetType,
      additionalData: {
        urlPath: window?.location?.pathname,
        currentCompany: actorAndTarget?.currentCompany,
        ...activity?.additionalData,
      },
      description: activity?.description
        ? `${actorAndTarget?.actorName} ${activity?.description}`
        : activity?.description,
      source: ACTIVITY_LOG_SOURCE,
      category: ActivityCategory.UserInteraction,
      timestamp: new Date().toISOString(),
    })
    return result
  } catch (err) {
    const error =
      err instanceof Error ? err : new Error('Failed to create activity log')
    console.error(error)
  }
}

/**
 * Helper function to conditionally show activity log popup or execute callback directly
 * @param options - Configuration object containing flags, dispatch, contentText, and callback
 * @param options.flags - Feature flags object
 * @param options.dispatch - Redux dispatch function
 * @param options.contentText - Text to display in the popup
 * @param options.onSubmit - Callback function to execute
 * @param options.required - Whether the popup is required (defaults to true)
 */
export const showActivityLogPopupOrExecute = ({
  flags,
  dispatch,
  contentText,
  onSubmit,
  required = true,
  isOpenActivityPopup = true,
}: {
  flags: Record<string, boolean>
  dispatch: (action: unknown) => void
  contentText: string
  onSubmit?: (formData: {
    reason?: string
    reasonCategory?: ActivityReasonCategory
    zendeskTicket?: string
  }) => void
  required: boolean
  isOpenActivityPopup?: boolean
}) => {
  if (
    flags?.['admin.activity-log-reason-popup-enabled'] &&
    isOpenActivityPopup
  ) {
    dispatch(
      openActivityLogPopup({
        contentText,
        onSubmit,
        required,
      })
    )
  } else {
    onSubmit?.({
      reason: '',
      reasonCategory: ActivityReasonCategory.OTHER,
      zendeskTicket: '',
    })
  }
}
